export type RegulationTab =
  | 'pasal'
  | 'perubahan'
  | 'ringkasan'
  | 'peralihan'
  | 'dasarHukum'
  | 'peraturanTerkait'
  | 'histori'
  | 'konsolidasi'
  | 'lampiran'
  | 'kepatuhan'
  | 'pihak';

export const TAB_LABELS: Record<RegulationTab, string> = {
  pasal: 'Isi Pasal',
  perubahan: 'Perubahan per Regulasi',
  ringkasan: 'Ringkasan',
  peralihan: 'Ketentuan Peralihan',
  dasarHukum: 'Dasar Hukum',
  peraturanTerkait: 'Peraturan Terkait',
  histori: 'Histori',
  konsolidasi: 'Konsolidasi',
  lampiran: 'Lampiran',
  kepatuhan: 'Kepatuhan',
  pihak: 'Para Pihak',
};

export const ALL_TABS: RegulationTab[] = [
  'konsolidasi',
  'pasal',
  'ringkasan',
  'dasarHukum',
  'peraturanTerkait',
  'histori',
  'lampiran',
];

/**
 * Kepatuhan and Para Pihak tabs on the regulation page. Hidden for now:
 * compliance work happens in the Compliance Checklist page, and the raw
 * per-regulation tabs confused users. Flip to show them again with the
 * admin / compliance-trial gate below.
 */
export const COMPLIANCE_TABS_ENABLED = false;

/** Whether a viewer sees the Kepatuhan and Para Pihak tabs. */
export function canSeeComplianceTabs(isAdmin: boolean, isComplianceTrial: boolean): boolean {
  return COMPLIANCE_TABS_ENABLED && (isAdmin || isComplianceTrial);
}

export interface GetValidTabsParams {
  hasOmnibusLaw: boolean;
  hasPerubahanPerRegulasi?: boolean;
  hasConsolidation: boolean;
  hasOmnibusConsolidation?: boolean;
  hasAttachments: boolean;
  hasDasarHukum: boolean;
  hasPeraturanTerkait: boolean;
  hasHistori: boolean;
  isAmendmentRegulation: boolean;
  /**
   * Compliance and Para Pihak tab gate: admins always see them,
   * compliance-trial users see Kepatuhan scoped to permit-tagged duties
   * (the list API filters server-side) and Para Pihak read-only
   * (extraction stays admin-only).
   */
  isAdmin?: boolean;
  isComplianceTrial?: boolean;
}

export function getValidTabs(params: GetValidTabsParams): RegulationTab[] {
  const tabs: RegulationTab[] = [];

  if (params.hasConsolidation || params.hasOmnibusConsolidation) {
    tabs.push('konsolidasi');
  }

  tabs.push('pasal');

  if (params.hasOmnibusLaw || params.hasPerubahanPerRegulasi) {
    const pasalIndex = tabs.indexOf('pasal');
    if (pasalIndex !== -1) {
      tabs.splice(pasalIndex + 1, 0, 'perubahan');
    }
  }

  tabs.push('ringkasan');

  if (params.isAmendmentRegulation) {
    tabs.push('peralihan');
  }

  if (params.hasDasarHukum) {
    tabs.push('dasarHukum');
  }

  if (params.hasPeraturanTerkait) {
    tabs.push('peraturanTerkait');
  }

  if (params.hasHistori) {
    tabs.push('histori');
  }

  if (params.hasAttachments) {
    tabs.push('lampiran');
  }

  if (canSeeComplianceTabs(Boolean(params.isAdmin), Boolean(params.isComplianceTrial))) {
    tabs.push('kepatuhan', 'pihak');
  }

  return tabs;
}

export function getDefaultTab(
  validTabs: RegulationTab[],
  hasConsolidation: boolean,
  hasOmnibusConsolidation?: boolean,
): RegulationTab {
  if ((hasConsolidation || hasOmnibusConsolidation) && validTabs.includes('konsolidasi')) {
    return 'konsolidasi';
  }
  return 'pasal';
}

export function isValidTab(tab: string, validTabs: RegulationTab[]): tab is RegulationTab {
  return validTabs.includes(tab as RegulationTab);
}
